/* eslint-disable @typescript-eslint/no-unused-vars */
import { type GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { cloneDeep } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CustomTooltipWithLabel,
  downloadFile,
  LinkButton,
  renderHeaderActionButtons,
  SidePopover,
  TableDashboard,
  useAppDispatch,
  useAppSelector
} from '../../../globalUtils/globalExports';
import {
  exportToCsvForAcUptimeReportChargerList,
  setAcUptimeChargerListTableProps,
  fetchAcUptimeReportChargerData,
  getAcChargerReducer,
  getAcFilterReducer,
  getAcReferenceDataReducer,
} from '../../rmsReduxStore/acReduxExports';
import {
  getCurrentAcFiltersValues,
  getCurrentScreenAppliedAcFiltersCount,
  getAcFilterSelectedValue,
  setAcFilterData,
} from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import {
  getGlobalReducer,
  setTableColumnPrefrences,
} from '../../../rmsReduxStore/reduxExports';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import '../chargers/chargers.css';
import { Id, toast } from 'react-toastify';
import { createBreadCrumbProps } from '../../../globalUtils/globalHooks';
import access from '../../../auth/service/AccessControl';
import { secondsToDHMS } from '../../../globalUtils/globalHooks';
import { Icon } from '../../../globalUtils/globalIcons';
import AcFilterLayout, { FilterProp } from '../../../globalUtils/FilterLayout/AcFilterLayout';

const AcUptimeReportDashboard = memo(function AcUptimeReportDashboard() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const enableVisibilityEdit = access?.hasChargerVisibilityEditAccess()
  const isOrgTypeCustomer = access?.isOrgTypeCustomer();
  const layouts = ['list'];
  const { chargerTableLoader, acUptimeChargerListTableProps, acUptimeReportList, acUptimeReportListCount } = useAppSelector(getAcChargerReducer);
  const {
    chargingStationReferenceData,
    chargerProductTypeReferenceData,
  } = useAppSelector(getAcReferenceDataReducer);
  const { screenFilters } = useAppSelector(getAcFilterReducer)
  const { screenTableColumnPrefrences } = useAppSelector(getGlobalReducer)
  const [layoutView, setLayoutView] = useState<string>('list');
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exportingFile, setExportingFile] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const chargerListTablePropsRef = useRef(acUptimeChargerListTableProps);

  useEffect(() => {
    chargerListTablePropsRef.current = acUptimeChargerListTableProps;
  }, [acUptimeChargerListTableProps]);

  const onChangeOfSearchText = useCallback(async (val) => {
    if (chargerListTablePropsRef?.current) {
      await layoutCallback(1, chargerListTablePropsRef?.current?.pageSize, chargerListTablePropsRef?.current?.view, chargerListTablePropsRef?.current?.sortBy, chargerListTablePropsRef?.current?.order, null, val)
    }
  }, [layoutView, chargerListTablePropsRef?.current]);

  const searchBoxConfiguration = useMemo(() => {
    return {
      searchFieldId: 'ac-manage-chargers-search-box',
      searchFieldName: 'ac-manage-chargers-search-box',
      handleSearch: onChangeOfSearchText,
    };
  }, [onChangeOfSearchText, layoutView, chargerListTablePropsRef?.current]);

  const headerActionBtns = renderHeaderActionButtons({
    actionBtns: [
      {
        id: 'add_filter_btn',
        isAccessible: true, // access?.chargers?.view(),
        headerActionBtnClick: () => setFiltersVisible(true),
        badgeCount: getCurrentScreenAppliedAcFiltersCount('acUptimeReports'),
      },
      {
        id: 'export_csv_btn',
        isAccessible: true, // access?.chargers?.export(),
        headerActionBtnClick: (event) => setExportAnchorEl(event.currentTarget),
      },
    ],
  });

  const getQuickDateRange = (type: '24H' | '7D' | '30D') => {
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    const start = new Date(end);
    switch (type) {
      case '24H':
        start.setDate(end.getDate() - 1);
        break;
      case '7D':
        start.setDate(end.getDate() - 7);
        break;
      case '30D':
        start.setDate(end.getDate() - 30);
        break;
    }
    return [
      start.toISOString(),
      end.toISOString(),
    ];
  };

  const quickDateSelectOptions = [
    { label: 'Last Day', value: '24H' },
    { label: 'Last 7 Days', value: '7D' },
    { label: 'Last 30 Days', value: '30D' },
    { label: 'Custom Range', value: 'CUSTOM' }
  ]

  const handleFiltersClosePopOver = (): void => {
    setFiltersVisible(false);
  }

  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['chargerVisibleId', 'ocppId'],
        headerRightDataConfig: {
          actionIconsComponent: false,
          statusIconComponent: true,
        },
      },
      contentDataConfig: [
        [
          {
            key: 'chargingStation',
            iconClassName: 'chargers__icon',
          },
          {
            key: 'customer',
            iconClassName: 'customer__management__icon',
          },
        ]
      ],
    };
  }, []);

  const getTableRows = useCallback((): IAcCharger[] => {
    return acUptimeReportList !== null && acUptimeReportList?.length > 0 ? acUptimeReportList : [];
  }, [acUptimeReportList]);

  const uptimeReportColumns = useMemo(() => {
    let columns = [
      {
        field: 'chargerVisibleId',
        flex: 1,
        maxWidth: 500,
        headerName: 'Charger',
        minWidth: 150,
        hideable: false,
        pinnable: false,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            rowData?.productType ?
              (<div
                className="individual__charger__wrap"
              >
                <LinkButton
                  linkBtnId={rowData?.chargerVisibleId}
                  link={'/ac/charger?chargerId=' + rowData.chargerVisibleId}
                  btnClassName="individual__charger__text"
                  btnText={rowData?.chargerVisibleId}
                  linkBtnState={{ chargerId: rowData?.id, charger: rowData }}
                />
              </div>) : (<div>{rowData?.chargerVisibleId}</div>)
          );
        },
      },
      {
        field: 'ocppId',
        headerName: 'OCPP Id',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row
          return (
            <CustomTooltipWithLabel label={rowData?.ocppId} labelId={rowData?.id} />
          )
        }
      },
      {
        field: 'chargingStation',
        headerName: 'Charging Station',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row
          return (
            <CustomTooltipWithLabel label={rowData?.chargingStation} labelId={rowData?.id} />
          )
        }
      },
      {
        field: 'customer',
        headerName: 'Customer',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
      },
      {
        field: 'productType',
        headerName: 'Product Type',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
      },
      {
        field: 'connectivity',
        headerName: 'Connectivity',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
      },
      {
        field: 'networkDownTime',
        headerName: 'Network DownTime (%)',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{rowData?.networkDownTime + '%'}</span>
            </div>
          );
        },
      },
      {
        field: 'totalNetworkFaultDuration',
        headerName: 'Network Fault Duration',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{secondsToDHMS(rowData?.totalNetworkFaultDuration)}</span>
            </div>
          );
        },
      },
      {
        field: 'totalGridFailDuration',
        headerName: 'Grid Fail Duration',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{secondsToDHMS(rowData?.totalGridFailDuration)}</span>
            </div>
          );
        },
      },
      {
        field: 'totalChargePointFaultDuration',
        headerName: 'Charge Point Fail Duration',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{secondsToDHMS(rowData?.totalChargePointFaultDuration)}</span>
            </div>
          );
        },
      },
      {
        field: 'maxPermissibleUptime',
        headerName: 'Max Permissible Uptime',
        flex: 1,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{secondsToDHMS(rowData?.maxPermissibleUptime)}</span>
            </div>
          );
        },
      },
      {
        field: 'networkUptime',
        headerName: 'Network Uptime (%)',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{rowData?.networkUptime + '%'}</span>
            </div>
          );
        },
      },
      {
        field: 'networkUptimeDuration',
        headerName: 'Network Uptime Duration',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{secondsToDHMS(rowData?.networkUptimeDuration)}</span>
            </div>
          );
        },
      },
      {
        field: 'overallUptime',
        headerName: 'Overall Uptime (%)',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<GridValidRowModel>): JSX.Element => {
          const rowData = params?.row;
          return (
            <div className="individual__charger__wrap">
              <span>{rowData?.overallUptime + '%'}</span>
            </div>
          );
        },
      },
      {
        field: 'rmsEnableStatus',
        headerName: 'Rms Enable Status ',
        flex: 1,
        sortable: true,
        pinnable: false,
        minWidth: 120,
      }
    ];
    if (isOrgTypeCustomer) {
      const fieldsToHideForCustomer = ['productType']
      columns = columns?.filter(column => !fieldsToHideForCustomer.includes(column?.field));
    }
    return columns;
  }, [isOrgTypeCustomer]);

  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, field: string, order: string, filterData, searchTerm) => {
    const tableProps: IAcChargerListTableProps = cloneDeep(chargerListTablePropsRef.current)
    const filters = filterData ? filterData : getCurrentAcFiltersValues('acUptimeReports');

    await dispatch(fetchAcUptimeReportChargerData({
      sortBy: field || chargerListTablePropsRef.current?.sortBy,
      order: order || chargerListTablePropsRef?.current?.order,
      pageSize,
      pageNumber,
      filters: filters,
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : chargerListTablePropsRef?.current?.searchTerm,
    }))
    if (tableProps) {
      tableProps.view = view || layoutView
      tableProps.sortBy = field || chargerListTablePropsRef?.current?.sortBy;
      tableProps.order = order || chargerListTablePropsRef?.current?.order;
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setAcUptimeChargerListTableProps(tableProps))
  }, [layoutView, chargerListTablePropsRef?.current, dispatch]);

  const handleRefresh = useCallback(async () => {
    if (chargerListTablePropsRef?.current) {
      await layoutCallback(chargerListTablePropsRef?.current?.pageNumber, chargerListTablePropsRef?.current?.pageSize, chargerListTablePropsRef?.current?.view, chargerListTablePropsRef?.current?.sortBy, chargerListTablePropsRef?.current?.order, null, chargerListTablePropsRef?.current?.searchTerm)
    }
  }, [layoutView, chargerListTablePropsRef?.current]);

  const handleColumnPrefrenceChange = useCallback(async (operation, data) => {
    await dispatch(setTableColumnPrefrences('acUptimeReports', operation, data))
  }, [dispatch])

  const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {
    const res = await setAcFilterData(val, filterKey, 'acUptimeReports', 'SET', isGlobal ? isGlobal : false)(dispatch)
    if (res?.message === 'Action dispatched successfully') {
      if (chargerListTablePropsRef?.current) {
        await layoutCallback(1, chargerListTablePropsRef?.current?.pageSize, chargerListTablePropsRef?.current?.view, chargerListTablePropsRef?.current?.sortBy, chargerListTablePropsRef?.current?.order, res?.filters, chargerListTablePropsRef?.current?.searchTerm)
      }
    }
  }, [chargerListTablePropsRef?.current, dispatch, layoutView])

  const uptimeFilterProps: FilterProp[] = useMemo(() => {
    const filterCustomDateRangeValue = getAcFilterSelectedValue(
      'acUptimeReportDateRangeInterval',
      'acUptimeReports',
      false
    );
    const selectedQuickDateRange = getAcFilterSelectedValue(
      'acUptimeQuickDateRange',
      'acUptimeReports',
      false
    )?.[0];

    const uptimeFilters: FilterProp[] = [
      {
        filterLabel: 'Product Type',
        filterType: 'dropdown',
        filterId: 'productType',
        filterDropdownProps: {
          selectValue: getAcFilterSelectedValue(
            'productType',
            'acUptimeReports',
            false
          ),
          selectOptions: chargerProductTypeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'productType', false);
          },
          selectDropDownId: 'ac-uptime-product-type-filter-dropdown',
          isMultiSelect: true,
        },
      },
      {
        filterLabel: 'Charging Stations',
        filterType: 'dropdown',
        filterId: 'chargingStationId',
        filterDropdownProps: {
          selectValue: getAcFilterSelectedValue(
            'chargingStationId',
            'acUptimeReports',
            false
          ),
          selectOptions: chargingStationReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
            handleFilterDataChange(val, 'chargingStationId', false);
          },
          selectDropDownId: 'ac-uptime-chargingStationId-filter-dropdown',
          isMultiSelect: true,
        },
      },
      {
        filterLabel: 'Date Range',
        filterType: 'dropdown',
        filterId: 'acUptimeQuickDateRange',
        filterDropdownProps: {
          selectValue: getAcFilterSelectedValue(
            'acUptimeQuickDateRange',
            'acUptimeReports',
            false
          ),
          selectOptions: quickDateSelectOptions,
          handleSelectOptionChange: async (val): Promise<void> => {
            if (val?.value === 'CUSTOM') {
              await setAcFilterData(
                val,
                'acUptimeQuickDateRange',
                'acUptimeReports',
                'SET',
                false
              )(dispatch);
              return;
            }
            const dateRange = getQuickDateRange(val.value);
            const res = await setAcFilterData(
              dateRange,
              'acUptimeReportDateRangeInterval',
              'acUptimeReports',
              'SET',
              false
            )(dispatch);
            await setAcFilterData(
              val,
              'acUptimeQuickDateRange',
              'acUptimeReports',
              'SET',
              false
            )(dispatch);
            if (res?.message === 'Action dispatched successfully') {
              layoutCallback(
                1,
                chargerListTablePropsRef.current.pageSize,
                chargerListTablePropsRef.current.view,
                chargerListTablePropsRef.current.sortBy,
                chargerListTablePropsRef.current.order,
                null,
                chargerListTablePropsRef.current.searchTerm
              );
            }
          },
          selectDropDownId: 'ac-uptime-quick-date-range-dropdown',
          isMultiSelect: false,
        },
      },
    ];

    if (selectedQuickDateRange?.value === 'CUSTOM') {
      uptimeFilters.push({
        filterLabel: 'Custom Date Range',
        filterType: 'dateFilter',
        filterId: 'acUptimeReportDateRangeInterval',
        dateFilterProps: {
          datePickerId: 'ac-uptime-custom-date-picker',
          datePickerClassName: 'uptime__custom__date__picker',
          selectsRange: true,
          startDate: filterCustomDateRangeValue?.[0]
            ? new Date(filterCustomDateRangeValue?.[0])
            : null,
          endDate: filterCustomDateRangeValue?.[1]
            ? new Date(filterCustomDateRangeValue?.[1])
            : null,
          onChange: (val): void => {
            if (!val) return;
            const [start, end] = val;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if ((start && start >= today) || (end && end >= today)) {
              toast.warn('Please select past dates only');
              return;
            }
            handleFilterDataChange(val, 'acUptimeReportDateRangeInterval', false);
          },
        },
      });
    }
    return uptimeFilters;
  }, [
    chargerProductTypeReferenceData,
    chargingStationReferenceData,
    screenFilters,
    chargerListTablePropsRef?.current,
    layoutView,
    handleFilterDataChange,
    dispatch,
    layoutCallback
  ]);

  const onResetButtonClick = useCallback(async (callListApi: boolean) => {
    const res = await setAcFilterData(null, '', 'acUptimeReports', 'CLEAR', false)(dispatch);
    if (res?.message === 'Action dispatched successfully') {
      if (chargerListTablePropsRef?.current && callListApi) {
        layoutCallback(1, chargerListTablePropsRef?.current?.pageSize, chargerListTablePropsRef?.current?.view, chargerListTablePropsRef?.current?.sortBy, chargerListTablePropsRef?.current?.order, res?.filters, chargerListTablePropsRef?.current?.searchTerm)
      }
    }
  }, [chargerListTablePropsRef?.current, dispatch, layoutCallback])

  const handleClose = (): void => {
    setExportAnchorEl(null);
  };

  const fileDownloadSuccessToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.success('File downloaded.');
  }, []);

  const fileDownloadFailureToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.warn('Something went wrong. Please export again.');
  }, []);

  const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
    if (exportingFile) {
      return;
    }
    try {
      setExportingFile(true);
      const response = await dispatch(exportToCsvForAcUptimeReportChargerList({
        fileType: fileType,
        filters: getCurrentAcFiltersValues('acUptimeReports')
      }));
      if (response?.status === 202 || response?.status === 200) {
        downloadFile({ url: response?.data?.url });
        fileDownloadSuccessToast()
      } else {
        fileDownloadFailureToast();
      }
    } finally {
      setExportingFile(false);
      setExportAnchorEl(null);
    }
  }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast, dispatch])

  const breadCrumbs = createBreadCrumbProps({
    breadCrumbProps: [
      {
        objectType: 'link',
        id: 'acchargers',
        linkBtnState: location?.state,
        handleOnClick: () => {
          setLayoutView('list')
        }
      }
    ]
  })

  const filterFooterBtns = useMemo(() => {
    return [
      {
        buttonText: 'Reset',
        buttonId: 'Reset',
        btnClassName: 'secondary__btn',
        handleClick: (): Promise<void> => onResetButtonClick(true),
        isDisabled: false,
        buttonVariant: 'outlined',
      },
    ];
  }, [onResetButtonClick]);

  useEffect(() => {
    layoutCallback(1, chargerListTablePropsRef?.current?.pageSize, chargerListTablePropsRef?.current?.view, chargerListTablePropsRef?.current?.sortBy, chargerListTablePropsRef?.current?.order, null, chargerListTablePropsRef?.current?.searchTerm)
  }, []);

  const NoData = () => {
    return (
      <div className="rms__table__dashboard__no__data">
        <Icon iconClassName='no__charger__data__icon' />
        <div className="rms__table__dashboard__no__charger__data">Data Not Found</div>
      </div>
    );
  }

  return (
    <>
      <TableDashboard
        tableId="ac-manage-chargers-table"
        tableClassName="manage__chargers__table"
        header={'Uptime Report'}
        searchBoxIncluded={true}
        searchBoxConfiguration={searchBoxConfiguration}
        gridColumns={uptimeReportColumns}
        tableRows={getTableRows()}
        mobileViewConfiguration={mobileViewConfiguration}
        layouts={layouts}
        layoutView={layoutView}
        headerActionBtns={headerActionBtns}
        listLayoutCallBack={layoutCallback}
        totalCount={acUptimeReportListCount ?? 0}
        showSkeleton={!acUptimeReportList}
        totalCountText={'chargers'}
        showLoader={chargerTableLoader}
        customNoDataComponent={NoData}
        pageNumber={acUptimeChargerListTableProps?.pageNumber}
        tablePageSize={acUptimeChargerListTableProps?.pageSize}
        sortField={acUptimeChargerListTableProps?.sortBy}
        sortOrder={acUptimeChargerListTableProps?.order}
        breadCrumbs={breadCrumbs}
        showRefreshButton={true}
        handleRefresh={handleRefresh}
        columnPrefrences={screenTableColumnPrefrences?.find((item) => (item.screen as string) === 'acUptimeReports')}
        handleColumnPrefrenceChange={handleColumnPrefrenceChange}
      />

      <Menu
        id="export-option-dropdown"
        anchorEl={exportAnchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className='export__option__dropdown'
        sx={{ top: '30px' }}
        open={Boolean(exportAnchorEl)}
        onClose={handleClose}
      >
        <MenuItem>
          <div className='export__btn-option' onClick={(e) => {
            e?.stopPropagation();
            onExportBtnClick('csv')
          }}>
            To CSV
          </div>
        </MenuItem>
        <MenuItem>
          <div className='export__btn-option' onClick={(e) => {
            e?.stopPropagation();
            onExportBtnClick('xlsx')
          }}>
            To Excel
          </div>
        </MenuItem>
      </Menu>

      {filtersVisible && <SidePopover
        id={'ac__manage__chargers__side__filters'}
        open={filtersVisible}
        containerClassName='manage__chargers__side__filters'
        handleClose={handleFiltersClosePopOver}
        headerText={' Add Filters'}
        btnsList={filterFooterBtns}
      >
        <AcFilterLayout
          requiredReferenceDataCallbacks={['productType', 'chargingStationId']}
          filters={uptimeFilterProps}
          handleFilterDataChange={handleFilterDataChange}
        />
      </SidePopover>
      }
    </>
  );
})

export default AcUptimeReportDashboard;
